import React, { useState, useCallback } from 'react';
import {
  checkActiveSurveyTab,
  registerActiveSurveyTab,
  unregisterActiveSurveyTab,
} from 'src/utils/surveySessionGuard';
import {
  SurveyConflictAlert,
  SurveyBlockedScreen,
} from 'src/components/SurveyConflictAlert';

/**
 * Custom hook to guard survey start pages against multi-tab collisions.
 *
 * @param {string} surveyTitle - The title/type of this survey (for display in alerts)
 */
export const useSurveyConflictGuard = (surveyTitle = 'Survey') => {
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [sessionBlocked, setSessionBlocked] = useState(false);
  const [activeSurveyName, setActiveSurveyName] = useState('');

  /**
   * Checks if another survey is open.
   * If yes -> blocks execution, opens modal, returns false.
   * If no -> registers tab, calls onProceed, returns true.
   *
   * @param {object} params - The incoming survey URL parameters
   * @param {function} onProceed - Callback to execute (setCookie, validateToken) if safe
   * @returns {Promise<boolean>} - true if proceeded, false if blocked
   */
  const checkConflictAndProceed = useCallback(
    async (params, onProceed) => {
      const incomingInfo = {
        inviteId: params?.inviteId || (params?.goalId ? String(params.goalId) : undefined),
        queueId: params?.queueId,
        surveyId: params?.surveyId || (params?.pulseId ? String(params.pulseId) : undefined),
        userId: params?.userId || params?.invitedUserId,
        surveyTitle: surveyTitle,
      };

      const conflictCheck = await checkActiveSurveyTab(incomingInfo);

      if (conflictCheck.hasConflict) {
        console.warn(
          '[SurveyConflictGuard] Another survey tab is active. Blocking /set-cookie.'
        );
        setActiveSurveyName(
          conflictCheck.activeSurvey?.surveyTitle || 'Another Survey'
        );
        setConflictModalOpen(true);
        setSessionBlocked(true);
        return false;
      }

      // No conflict: register this tab as the active survey tab
      registerActiveSurveyTab(incomingInfo);

      if (onProceed && typeof onProceed === 'function') {
        await onProceed();
      }
      return true;
    },
    [surveyTitle]
  );

  const handleCloseAlert = useCallback(() => {
    setConflictModalOpen(false);
  }, []);

  const SurveyConflictAlertComponent = useCallback(() => {
    return (
      <SurveyConflictAlert
        open={conflictModalOpen}
        onClose={handleCloseAlert}
        activeSurveyName={activeSurveyName}
      />
    );
  }, [conflictModalOpen, handleCloseAlert, activeSurveyName]);

  const SurveyBlockedScreenComponent = useCallback(() => {
    return <SurveyBlockedScreen activeSurveyName={activeSurveyName} />;
  }, [activeSurveyName]);

  return {
    conflictModalOpen,
    sessionBlocked,
    activeSurveyName,
    checkConflictAndProceed,
    handleCloseAlert,
    unregisterActiveSurveyTab,
    SurveyConflictAlertComponent,
    SurveyBlockedScreenComponent,
  };
};

export default useSurveyConflictGuard;
